import { yupResolver } from '@hookform/resolvers/yup';
import { usePostAddWorkspace } from 'api/workspace';
import ErrorBox from 'components/error/ErrorBox';
import InputField from 'components/fields/InputField';
import HeaderDoc from 'components/header/HeaderDoc';
import { CustomModal, CustomModalButtons } from 'components/modal';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { cn } from 'utils/class-merge';
import { isSaaS } from 'utils/get-editions';
import useCustomToast from 'utils/use-toast';
import { REGIONS } from 'variables/regions';
import { RegionSelect } from 'views/auth/signUp/SignUpDefault/components';
import * as yup from 'yup';
import { RegionSelectOption } from './WorkspaceSwitcher';

type FormAddWorkspace = {
  name: string;
  region: string | null;
};

export const schema = yup.object({
  name: yup
    .string()
    .required('name is required')
    .matches(
      /^[a-zA-Z0-9_ ]*$/,
      'name can only contain letters, numbers, underscores, and spaces',
    )
    .min(2, 'name must be at least 2 characters'),
});

function AddWorkspaceModal({
  isOpen,
  onClose,
  setSelectedWorkSpace,
  handleSwitchWorkSpaces,
}: {
  onClose: () => void;
  isOpen: boolean;
  setSelectedWorkSpace: React.Dispatch<
    React.SetStateAction<RegionSelectOption>
  >;
  handleSwitchWorkSpaces: (id: number, region: string | null) => void;
}) {
  const postAddWorkspace = usePostAddWorkspace();
  const toast = useCustomToast();
  const saasDeployment = isSaaS();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    setFocus,
  } = useForm<FormAddWorkspace>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    defaultValues: {
      region: saasDeployment ? REGIONS['US_WEST_2'] : null,
    },
  });

  const handleClose = () => {
    clearErrors('name');
    reset({
      name: '',
      region: saasDeployment ? REGIONS['US_WEST_2'] : null,
    });
    onClose();
  };

  const onSubmit: SubmitHandler<FormAddWorkspace> = values => {
    const data = {
      name: values.name,
      region: values.region,
    };

    postAddWorkspace.mutate(data, {
      onSuccess: response => {
        const client = response.data?.client;

        setSelectedWorkSpace({
          label: String(client?.name),
          value: String(client?.id),
          region: String(client?.region),
        });
        handleSwitchWorkSpaces(client?.id, client?.region);
        toast.success('New workspace has been created');
        handleClose();
      },
      onError: error => {
        toast.showError(error);
      },
    });
  };

  React.useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setFocus('name');
      });
    }
  }, [setFocus, isOpen]);

  return (
    <CustomModal
      disabledModalCloseButton={postAddWorkspace.isLoading}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <h1 className="mb-4 text-2xl font-bold">Create Workspace</h1>

      <p className={cn('text-muted-foreground text-sm mb-4')}>
        A workspace is a self-contained unit for organizing resources.
        <HeaderDoc
          link="https://docs.engagespot.co/docs/concepts/workspace"
          text="Learn more"
        />
      </p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <InputField<FormAddWorkspace>
          variant="modal"
          extra="mb-3"
          label={'Workspace Name'}
          placeholder="Eg: delivery-app"
          type="text"
          showIsRequiredAsterisk={true}
          register={register}
          name={'name'}
        />
        {errors.name && <ErrorBox error={errors.name} />}

        {saasDeployment && (
          <div className="my-5">
            <RegionSelect
              control={control}
              labelClassName="text-white text-sm ml-1.5"
            />
          </div>
        )}

        <CustomModalButtons
          handleClose={handleClose}
          primaryButtonText="Create"
          loading={postAddWorkspace.isLoading}
        />
      </form>
    </CustomModal>
  );
}

export default AddWorkspaceModal;
